import { OrchestratorThread } from '../agentic/orcThread';
import { BaseSubscription } from './base_subscription';
export class Subscription extends BaseSubscription {
    constructor(connectionID, channelConfig, websocketHandler, process = "subscribe") {
        super(connectionID, channelConfig, websocketHandler, process);
        this.threads = new Map();
    }
    listen(callback) {
        this._drainAndAttach(this.messageBuffer, "all", callback);
    }
    attachThreadListener(threadId, callback) {
        var _a;
        const buf = (_a = this.threadBuffers.get(threadId)) !== null && _a !== void 0 ? _a : new Map();
        this._drainAndAttach(buf, `${threadId}-all`, callback);
        this.threadBuffers.delete(threadId);
    }
    bind(event, callback) {
        this._drainEventAndAttach(this.messageBuffer, event, event, callback);
    }
    attachThreadBind(threadId, event, callback) {
        var _a;
        const buf = (_a = this.threadBuffers.get(threadId)) !== null && _a !== void 0 ? _a : new Map();
        this._drainEventAndAttach(buf, event, `${threadId}-${event}`, callback);
    }
    remove(event, callback) {
        if (callback)
            this.off(event, callback);
        else
            this.removeAllListeners(event);
        this.messageBuffer.delete(event);
    }
    detachThreadListener(threadId, event, callback) {
        const attachEvent = `${threadId}-${event}`;
        if (callback)
            this.off(attachEvent, callback);
        else
            this.removeAllListeners(attachEvent);
        const buf = this.threadBuffers.get(threadId);
        if (buf)
            buf.delete(event);
    }
    async push(event, data, options) {
        return super.push(event, data, options);
    }
    /**
     * Internal method to handle incoming messages for this subscription.
     * @param event The event name.
     * @param data The data received.
     */
    async handleMessage(event, payload) {
        var _a;
        const { return_flag } = payload;
        if (return_flag == "SA") {
            this.handleMessageAcks(event, return_flag, payload);
            return;
        }
        this.acknowledge(payload, "MA");
        if (this.channelConfig.channelType == "secure") {
            const { data: pubRes } = await this.websocketHandler.pushForSecureLine("secured_public_key", {
                "username": payload["from_username"]
            }, true);
            if (pubRes['status'] == 'unsuccessfull') {
                throw Error(pubRes['error']);
            }
            payload['data'] = await this.websocketHandler.decrypt(payload['data'], pubRes['public_key']);
        }
        let content = {};
        if (payload.hasOwnProperty('data')) {
            content = JSON.parse(payload['data']);
        }
        else {
            content = JSON.parse(payload);
        }
        const humanFeedbackRequest = return_flag === "requestFeedback" ||
            event === "human_input_request" ||
            (content && content.type === "human_input_request");
        if (humanFeedbackRequest) {
            content = {
                ...content,
                reply: (replyData) => this.sendHumanFeedback(payload, replyData),
            };
        }
        let emitConfig = {
            thread_id: (_a = payload["thread_id"]) !== null && _a !== void 0 ? _a : ""
        };
        if (event === 'art_presence') {
            this.emitEvent('art_presence', content, emitConfig);
        }
        else if (this.isSubscribed && event != 'art_presence') {
            const threadId = payload.thread_id;
            const eventKey = threadId ? `${threadId}-${event}` : event;
            const allKey = threadId ? `${threadId}-all` : "all";
            const hasSpecific = this.listeners(eventKey).length > 0;
            const hasAll = this.listeners(allKey).length > 0;
            if (hasSpecific || hasAll) {
                if (hasSpecific)
                    this.emitEvent(event, content, emitConfig);
                if (hasAll)
                    this.emitEvent("all", { event, content }, emitConfig);
                this.acknowledge(payload, "CA");
            }
            else {
                const { id, from, channel, to, pipeline_id, thread_id, attempt_id, interceptor_name, to_username } = payload;
                let buffPayload = {
                    id, from, channel, to, pipeline_id, thread_id, attempt_id, interceptor_name, to_username,
                    content
                };
                this.addToBuffer(event, buffPayload);
            }
        }
    }
    emitEvent(event, content, configs) {
        if (configs && (configs === null || configs === void 0 ? void 0 : configs.thread_id)) {
            event = `${configs === null || configs === void 0 ? void 0 : configs.thread_id}-${event}`;
        }
        this.emit(event, content);
    }
    addToBuffer(event, bufPayload) {
        const threadId = bufPayload === null || bufPayload === void 0 ? void 0 : bufPayload.thread_id;
        if (threadId) {
            let inner = this.threadBuffers.get(threadId);
            if (!inner) {
                inner = new Map();
                this.threadBuffers.set(threadId, inner);
            }
            const buf = inner.get(event) || [];
            buf.push(bufPayload);
            inner.set(event, buf);
        }
        else {
            const buf = this.messageBuffer.get(event) || [];
            buf.push(bufPayload);
            this.messageBuffer.set(event, buf);
        }
    }
    _drainAndAttach(buffer, allEvent, callback) {
        buffer.forEach((msgs, evt) => msgs.forEach(m => {
            callback({ event: evt, content: m.content });
            this.acknowledge(m, "CA");
        }));
        buffer.clear();
        this.on(allEvent, callback);
    }
    _drainEventAndAttach(buffer, bufferEvent, // key inside the buffer (clean event name)
    attachEvent, // event to subscribe to on the emitter
    callback) {
        (buffer.get(bufferEvent) || []).forEach(m => {
            callback(m.content);
            this.acknowledge(m, "CA");
        });
        buffer.delete(bufferEvent);
        this.on(attachEvent, callback);
    }
    thread(thread_id) {
        if (!this.channelConfig.orchestratorEnabled) {
            throw Error("Thread works only in case of orchestrator enabled channels");
        }
        return this._threadUnchecked(thread_id);
    }
    /** @internal Bypasses the orchestratorEnabled gate. Intended for callers
     *  that have already committed to orchestrator semantics (e.g. the
     *  Orchestrator workflow class). Do not call directly from app code. */
    _threadUnchecked(thread_id) {
        if (thread_id) {
            const existing = this.threads.get(thread_id);
            if (existing && !existing.isDisposed())
                return existing;
        }
        const t = new OrchestratorThread(this, thread_id);
        this.threads.set(t.threadId, t);
        return t;
    }
    getThread(threadId) {
        return this.threads.get(threadId);
    }
    _unregisterThread(threadId) {
        this.threads.delete(threadId);
        this.threadBuffers.delete(threadId);
    }
    sendHumanFeedback(originalReq, replyData) {
        var _a;
        const conn = this.websocketHandler.getConnection();
        const reply = {
            channel: originalReq.channel,
            namespace: originalReq.namespace,
            id: originalReq.id,
            ref_id: originalReq.ref_id,
            from: (_a = conn === null || conn === void 0 ? void 0 : conn.ConnectionId) !== null && _a !== void 0 ? _a : "",
            to: originalReq.from ? [originalReq.from] : [],
            to_username: originalReq.to_username,
            from_username: originalReq.from_username,
            return_flag: "HF",
            thread_id: originalReq.thread_id,
            node_id: originalReq.node_id,
            iteration_id: originalReq.iteration_id,
            root_workflow_id: originalReq.root_workflow_id,
            agent_node_id: originalReq.agent_node_id,
            agent_id: originalReq.agent_id,
            environment_id: originalReq.environment_id,
            pipeline_id: originalReq.pipeline_id,
            attempt_id: originalReq.attempt_id,
            interceptor_name: originalReq.interceptor_name,
            content: JSON.stringify(replyData),
        };
        this.websocketHandler.sendMessage(JSON.stringify(reply));
    }
}
